/**
 * The Map Review surface's one copy of the symbology catalog
 * (doc/in-progress/site-tagging-and-symbology.md §4.5/§7.3).
 *
 * The server resolves the whole precedence order once per session and answers
 * it as a single document, so the client reads it once and holds it: the Site
 * layers' paint expressions and the Symbology pane are built from the same
 * object, and a member's override write is followed by one forced reload rather
 * than each surface asking for its own copy.
 *
 * The module is deliberately not a store: it holds no component state, takes no
 * arguments about who is reading, and answers a `ref` any caller can watch. The
 * only mutable thing is the catalog itself — `loadSymbologyCatalog` replaces it
 * in place, which is what lets the map repaint without the reader passing
 * anything down.
 */

import { ref } from "vue";

import { apiClient } from "@/client";
import type { SymbologyCatalog } from "@/type/api";

const CATALOG_URL = "/api/symbology/catalog";

/**
 * The catalog the session has resolved, or null before the first answer. A
 * failed read leaves it at whatever was there — the map keeps drawing the
 * catalog it already had rather than falling back to the base rendering over a
 * transient fault.
 */
export const symbologyCatalog = ref<SymbologyCatalog | null>(null);

/**
 * The read in flight, if any. Two panes mounting together would otherwise
 * issue two identical requests; sharing the promise means the server answers
 * once and both callers resolve to the same catalog.
 */
let inflight: Promise<SymbologyCatalog> | null = null;

/**
 * Resolve the catalog for this session.
 *
 * A cold load happens once: the answer is kept and later calls return it. A
 * forced load (`force: true`) is the refresh: a write that changed what the
 * server resolves — an override, a binding, a symbol — leaves this copy stale,
 * so the write's caller reloads it before the map repaints.
 */
export function loadSymbologyCatalog(force = false): Promise<SymbologyCatalog> {
	if (!force && symbologyCatalog.value !== null) {
		return Promise.resolve(symbologyCatalog.value);
	}
	if (inflight !== null) return inflight;
	inflight = apiClient
		.JSONGet<SymbologyCatalog>(CATALOG_URL)
		.then((catalog) => {
			symbologyCatalog.value = catalog;
			return catalog;
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
}
